/**
 * Look Lab API functions – outfit CRUD, generation, and matching.
 */
import { apiFetch, buildUrl, PROXY_BASE, AI_TIMEOUT } from "@/shared/api/client";
import type { ApiResponse } from "@/shared/api/types";
import type {
  Outfit,
  OutfitGenerateResponse,
  OutfitsParams,
  OutfitsListResponse,
} from "./types";

// ── Outfit queries ──────────────────────────────────────────────────────

/**
 * Get user's outfits (paginated)
 */
export async function getOutfits(
  params: OutfitsParams
): Promise<ApiResponse<OutfitsListResponse>> {
  return apiFetch(
    buildUrl(`${PROXY_BASE}/outfits`, params as unknown as Record<string, unknown>)
  );
}

/**
 * Get outfit by ID
 */
export async function getOutfit(id: string): Promise<ApiResponse<Outfit>> {
  return apiFetch(`${PROXY_BASE}/outfits/${id}`);
}

/**
 * Check for existing outfits containing a wardrobe item.
 * Used by Look Lab to avoid regenerating outfits.
 */
export async function getExistingOutfits(params: {
  userId: string;
  anchorItemId: string;
}): Promise<ApiResponse<{ hasExisting: boolean; outfits: Outfit[] }>> {
  return apiFetch(
    buildUrl(
      `${PROXY_BASE}/outfits/existing`,
      params as Record<string, unknown>
    )
  );
}

// ── Outfit mutations ────────────────────────────────────────────────────

/**
 * Generate AI-powered outfit combinations based on anchor item.
 * Cost: 5 Hoku Coins (Feature Code: HOKU003)
 */
export async function generateOutfit(request: {
  userId: string;
  anchorItemId: string;
  maxOutfits?: number;
  forceRegenerate?: boolean;
}): Promise<ApiResponse<OutfitGenerateResponse>> {
  return apiFetch(`${PROXY_BASE}/outfits/generate`, {
    method: "POST",
    body: JSON.stringify(request),
    timeout: AI_TIMEOUT,
  });
}

/**
 * Delete outfit
 */
export async function deleteOutfit(
  id: string
): Promise<ApiResponse<{ deleted: boolean }>> {
  return apiFetch(`${PROXY_BASE}/outfits/${id}`, {
    method: "DELETE",
  });
}

/**
 * Toggle outfit like status
 */
export async function toggleOutfitLike(
  id: string
): Promise<ApiResponse<{ liked: boolean; disliked: boolean }>> {
  return apiFetch(`${PROXY_BASE}/outfits/${id}/like`, {
    method: "POST",
  });
}

/**
 * Toggle outfit dislike status
 */
export async function toggleOutfitDislike(
  id: string
): Promise<ApiResponse<{ disliked: boolean; liked: boolean }>> {
  return apiFetch(`${PROXY_BASE}/outfits/${id}/dislike`, {
    method: "POST",
  });
}

/**
 * Toggle outfit "I wore this" state. On the false→true transition the
 * backend increments wornCount and stamps lastWornAt; on true→false only
 * the boolean flips so historical wear data is preserved.
 */
export async function toggleOutfitWorn(
  id: string
): Promise<ApiResponse<{ hasWorn: boolean; wornCount: number; lastWornAt: string | null }>> {
  return apiFetch(`${PROXY_BASE}/outfits/${id}/worn`, {
    method: "POST",
  });
}
